// Profile notes: short, dated, user-authored entries shown on a seller's
// public profile. Notes live on the (publicly readable) users/{uid} doc, so
// they are world-readable by design — treat every note as public. The owner
// can pin notes, which surface above the profile tabs.

export type ProfileNote = {
    id: string;
    /**
     * Rich-text HTML from the product long description's editor, in the same
     * rich mode (images, video players, tables). ALWAYS render it through
     * `sanitizeNoteHtml()`: these notes come off a public-read doc written
     * straight from the client, so the render-time sanitize is the only gate
     * on them. Notes written before the editor landed are plain text;
     * `noteHtml()`, which `sanitizeNoteHtml()` runs first, detects that shape
     * and escapes it instead of trusting it as markup.
     */
    text: string;
    /** ISO 8601 timestamp of when the note was created. */
    createdAt: string;
    pinned: boolean;
};

/** Per-note cap on *visible* characters (what the editor's counter shows;
 *  markup, images and video players don't count against it). The same 5,000 a
 *  product's long description has (owner decision 2026-10-09). UI-only
 *  (firestore.rules cannot iterate a list to check each entry), like
 *  BIO_MAX_CHARS for the bio, whose raw HTML is additionally capped by bioValid
 *  in the rules because it is one string. */
export const NOTE_MAX_LENGTH = 5000;

/** Cap on the stored HTML of one note. Notes are written in the rich editor,
 *  and five storage links, five hardened player frames and a table are all
 *  markup on top of the visible text, so this sits at ten times
 *  NOTE_MAX_LENGTH. The composer refuses a note past it (noteSaveProblem)
 *  rather than storing one that would be cut, and normalizeNotes cuts anything
 *  longer on display, which bounds what a forged write can put on a public
 *  profile page. */
export const NOTE_MAX_HTML_LENGTH = 50_000;

/** Cap on all of a profile's notes together, in stored bytes. Every note lives
 *  in one array on users/{uid}: a document Firestore caps at 1 MiB, that every
 *  storefront view loads, and that /search downloads for every seller it
 *  lists. 256KB keeps the notes to a quarter of that document, so the rest of
 *  the profile always fits, and bounds what one seller's notes add to /search.
 *  Composer-only, like the caps above: rules cannot measure a list. */
export const NOTES_MAX_TOTAL_BYTES = 256 * 1024;

const utf8 = new TextEncoder();

/** The stored size of a profile's notes, counted the way Firestore counts a
 *  string: in UTF-8 bytes. Only the HTML is counted. The id, date and pin flag
 *  are a fixed few dozen bytes a note, which the document's headroom past
 *  NOTES_MAX_TOTAL_BYTES absorbs even at MAX_NOTES. */
export function notesStoredBytes(notes: readonly ProfileNote[]): number {
    return notes.reduce((sum, note) => sum + utf8.encode(note.text).length, 0);
}

export type NoteSaveProblem = 'tooLong' | 'profileFull';

/** Why a note cannot be saved, or null when it can. `html` is the note as it
 *  will be stored; `before` and `after` are the profile's notes without and
 *  with the change. A change that does not grow the notes is always allowed,
 *  so a profile already over the total (a legacy one, or one the total landed
 *  on) can still trim, pin and delete its way back under it. */
export function noteSaveProblem(
    html: string,
    before: readonly ProfileNote[],
    after: readonly ProfileNote[],
): NoteSaveProblem | null {
    if (html.length > NOTE_MAX_HTML_LENGTH) return 'tooLong';
    const total = notesStoredBytes(after);
    if (total > NOTES_MAX_TOTAL_BYTES && total > notesStoredBytes(before)) return 'profileFull';
    return null;
}

/** Hard cap on how many notes one profile may hold. Enforced BOTH here (so the
 *  composer disables itself with an explanation) and in firestore.rules, which
 *  is the real gate — notes are written straight from the client to the
 *  public-read users/{uid} doc, so a forged write bypasses everything in this
 *  file. Keep this number in sync with `notesWithinCap()` in firestore.rules. */
export const MAX_NOTES = 500;

/** Minimum gap between two note *creations*. Edits, pins and deletes are not
 *  throttled — only growth of the list. Mirrored by `noteAddAllowed()` in
 *  firestore.rules, which enforces it against the server clock; the client copy
 *  exists so the user sees a countdown instead of a permission-denied error. */
export const NOTE_ADD_COOLDOWN_MS = 30_000;

/** Milliseconds still to wait before another note may be added, given the
 *  epoch-ms timestamp of the last successful add (null = never added). */
export function noteAddCooldownRemainingMs(lastAddAt: number | null, now = Date.now()): number {
    if (lastAddAt === null) return 0;
    return Math.max(0, lastAddAt + NOTE_ADD_COOLDOWN_MS - now);
}

export function createNoteId(): string {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
    } catch {
        /* fall through to the timestamp-based id */
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// Any element-looking tag means the note came from the rich-text editor.
// Anything else is a legacy plain-text note (or a forged value), and gets
// escaped rather than trusted as markup.
const HTML_TAG_RE = /<\/?[a-z][a-z0-9]*(?:\s[^>]*)?\/?>/i;

function escapeHtml(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * The HTML to render for a note's stored `text`. Editor-authored notes pass
 * through untouched; legacy plain-text notes are escaped and given the paragraph
 * / line-break structure their whitespace implied, so they read the same as they
 * did under `whitespace-pre-wrap`.
 *
 * The result is still untrusted. Render notes through `sanitizeNoteHtml()`
 * (src/lib/sanitize-html.ts), which calls this first, so a render site hands it
 * the stored text as it is. Never `sanitizeHtml()`: that strict pass strips
 * every image, player, table and divider a note can hold.
 */
export function noteHtml(text: string): string {
    if (HTML_TAG_RE.test(text)) return text;
    return text
        .split(/\n{2,}/)
        .map(block => block.trim())
        .filter(Boolean)
        .map(block => `<p>${escapeHtml(block).replace(/\n/g, '<br />')}</p>`)
        .join('');
}

/** Visible text of a note's HTML, for length checks and blank detection. */
export function noteTextContent(html: string): string {
    return html
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/** True when a note shows nothing: no visible text and no image or video. An
 *  empty editor still reports `<p></p>`, so this is what the save/add buttons
 *  gate on. A screenshot or a clip with no caption is a note; a lone divider
 *  or an empty table is not. */
export function isNoteBlank(html: string): boolean {
    return noteTextContent(html).length === 0 && !/<(?:img|iframe)\b/i.test(html);
}

function toMillis(iso: string): number {
    const t = new Date(iso).getTime();
    return Number.isNaN(t) ? 0 : t;
}

/** Full list order for the Notes tab: pinned first, then newest to oldest. */
export function sortNotesForList(notes: ProfileNote[]): ProfileNote[] {
    return [...notes].sort((a, b) => {
        if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
        return toMillis(b.createdAt) - toMillis(a.createdAt);
    });
}

/** Just the pinned notes, newest first — for the strip above the tabs. */
export function pinnedNotes(notes: ProfileNote[]): ProfileNote[] {
    return notes
        .filter(n => n.pinned)
        .sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
}

/** Coerce whatever is on the user doc into a clean ProfileNote[]. Guards
 *  against legacy / forged shapes so the profile pages never crash on bad
 *  data from the public-read doc. */
export function normalizeNotes(raw: unknown): ProfileNote[] {
    if (!Array.isArray(raw)) return [];
    return raw
        .filter((n): n is Record<string, unknown> =>
            !!n && typeof n === 'object' && typeof (n as any).text === 'string')
        // Truncate to the cap as well as validate shapes: a doc written before
        // the cap existed (or by a client that bypassed it) must not turn into
        // an unbounded render on a public profile page.
        .slice(0, MAX_NOTES)
        .map(n => ({
            id: typeof n.id === 'string' && n.id ? n.id : createNoteId(),
            // Slicing HTML can cut a tag in half; that is fine because every
            // render path sanitizes, and DOMPurify drops the partial tag.
            text: String(n.text).slice(0, NOTE_MAX_HTML_LENGTH),
            createdAt: typeof n.createdAt === 'string' ? n.createdAt : new Date().toISOString(),
            pinned: !!n.pinned,
        }));
}
